/**
 * collapsedHistory.ts — the collapsed-transcript arithmetic, as a leaf.
 *
 * A re-opened thread renders only the newest VISIBLE_MESSAGE_COUNT rows;
 * everything older sits in the zero-height reveal-older container until the
 * reader expands it. P0 thread-durability (docs/PLAN-non-direct-app-
 * connections-20260901.md §3.3, D6 mechanism (a) — the Fitbit diagnosis of
 * 2026-09-02: the connect card sat 12th from newest in a 15-message thread,
 * intact in BOTH stores, behind the collapsed container whose "load earlier
 * messages" control was itself hidden). The rules live here so they are
 * pinnable without rendering the whole chat surface:
 *   - which row index is collapsed for a given transcript length;
 *   - the position-from-newest a diagnosis reports (1 = newest);
 *   - the load-earlier control's visibility, which carries NO collapsed-state
 *     term — server history must stay reachable while local history is
 *     collapsed (the control used to hide behind `showAllMessages`);
 *   - whether the collapsed region is hiding a row the reader must be able to
 *     act on (a standing connect tile), which is what makes the thread open
 *     expanded instead.
 *
 * scripts/ops/diagnose-connect-tile.mjs carries a byte-for-byte port of the
 * arithmetic (it is plain node, no TS) — keep the two in lockstep.
 *
 * Leaf module: no React, no component imports.
 */
export const VISIBLE_MESSAGE_COUNT = 10;

/** Index of the first row that renders in the always-visible tail. */
export function olderBoundary(total: number): number {
  return Math.max(0, total - VISIBLE_MESSAGE_COUNT);
}

/** True when the row at `index` (0 = oldest) sits inside the collapsed
 *  reveal-older container on a fresh open. */
export function isInCollapsedHistory(index: number, total: number): boolean {
  return index < olderBoundary(total);
}

/** 1 = the newest row; the number a diagnosis quotes ("10th from newest"). */
export function positionFromNewest(index: number, total: number): number {
  return total - index;
}

/**
 * The "load earlier messages" control's visibility. Deliberately a function
 * of the SERVER paging state alone: whether local history is collapsed is
 * not an input, so the control can never again hide behind the reveal pill.
 */
export function loadEarlierControlVisible(input: {
  threadSwitcherEnabled: boolean;
  earlierMessagesAvailable: boolean;
}): boolean {
  return Boolean(input.threadSwitcherEnabled && input.earlierMessagesAvailable);
}

/**
 * Is the collapsed region hiding a row the reader must be able to act on?
 *
 * The load-earlier control only appears when the SERVER holds pages older
 * than the loaded window — which the diagnosed Fitbit thread (15 messages,
 * card 12th from newest) does not, so making that control always reachable
 * did nothing for it (review 2026-09-02). This predicate is the lever that
 * does: when a standing connect tile sits in the collapsed region, the thread
 * opens with local history EXPANDED (through the same restore idiom the
 * persisted view restore uses — no reveal animation, no data change), so the
 * tile is on screen instead of inside a zero-height container.
 *
 * Pure and caller-supplied: the predicate decides what "standing" means, this
 * module only owns the arithmetic.
 */
export function collapsedRegionHoldsRow(
  total: number,
  matches: (index: number) => boolean,
): boolean {
  const boundary = olderBoundary(total);
  for (let i = 0; i < boundary; i += 1) {
    if (matches(i)) return true;
  }
  return false;
}
